/**
 * Direct AI API Client for IntentDrive.ai
 * Supports multi-model approach with all AI providers
 */

import OpenAI from 'openai';
import { supabase } from '@/integrations/supabase/client';
import {
  AIMessage,
  ProviderConfig,
  AIServicesConfig,
  AIProvider,
  AIChatbotSettings,
  OpenAIConfig
} from '@/types/ai';

// Re-export AIMessage for use by hooks
export type { AIMessage };

export interface AICompletionParams {
  messages: AIMessage[];
  config?: ProviderConfig;
  temperature?: number;
  max_tokens?: number;
  stream?: boolean;
  response_format?: { type: 'json_object' | 'text' };
}

export interface AIValidationParams {
  question: string;
  questionType?: string;
  response: string;
  conversationContext?: AIMessage[];
  chatbotId?: string;
  aiConfig?: AIServicesConfig;
  validationSettings?: {
    strictValidation?: boolean;
    confidenceThreshold?: number;
    allowPartialMatches?: boolean;
  };
}

export interface AIValidationResult {
  isValid: boolean;
  confidenceScore: number;
  suggestedFollowUp?: string;
  reasonForRejection?: string;
  extractedValue?: string;
}

export interface ProviderClient {
  openai?: OpenAI;
  // We'll add other providers as needed
}

class DirectAIClient {
  private providerClients: ProviderClient = {};
  private globalConfig: Record<string, any> = {};

  constructor() {
    this.initializeGlobalConfig();
  }

  private async initializeGlobalConfig() {
    try {
      // Get global AI provider configurations
      const { data: globalConfigs, error } = await supabase.rpc(
        'get_global_ai_provider_settings'
      );

      if (!error && globalConfigs) {
        this.globalConfig = globalConfigs.reduce((acc: Record<string, any>, config: any) => {
          acc[config.provider_name] = config;
          return acc;
        }, {});
        console.log('✅ Global AI configurations loaded for providers:', Object.keys(this.globalConfig));
      } else if (error) {
        console.warn('⚠️ Global AI provider settings function not available:', error.message);
        // Gracefully handle missing function - AI integration will work with chatbot-specific settings
      }
    } catch (error) {
      console.warn('⚠️ Could not load global AI configurations:', error);
    }
  }

  private async getProviderClient(config: ProviderConfig, chatbotSettings?: AIChatbotSettings): Promise<any> {
    const { provider } = config;

    // Get API key from chatbot settings first, then fall back to global config
    let apiKey: string | undefined;
    let providerConfig: any = {};

    if (chatbotSettings?.aiServices?.providerKeys) {
      const keys = chatbotSettings.aiServices.providerKeys;
      switch (provider) {
        case 'openai':
          apiKey = keys.openai;
          break;
        case 'anthropic':
          apiKey = keys.anthropic;
          break;
        case 'google':
          apiKey = keys.google;
          break;
        case 'azure':
          apiKey = keys.azure?.apiKey;
          providerConfig = { resourceName: keys.azure?.resourceName };
          break;
        case 'mistral':
          apiKey = keys.mistral;
          break;
        case 'deepseek':
          apiKey = keys.deepseek;
          break;
        case 'custom':
          apiKey = keys.custom?.apiKey;
          providerConfig = { endpoint: keys.custom?.endpoint };
          break;
      }
    }

    // Fall back to global configuration
    if (!apiKey && this.globalConfig[provider]) {
      apiKey = this.globalConfig[provider].api_key;
      if (provider === 'azure') {
        providerConfig = {
          resourceName: this.globalConfig[provider].resource_name
        };
      }
    }

    if (!apiKey) {
      throw new Error(`No API key configured for provider: ${provider}`);
    }

    // Create and cache provider client
    switch (provider) {
      case 'openai':
        if (!this.providerClients.openai) {
          this.providerClients.openai = new OpenAI({
            apiKey,
            dangerouslyAllowBrowser: true
          });
        }
        return this.providerClients.openai;

      case 'azure':
        // Azure OpenAI client
        const azureConfig = config as any;
        return new OpenAI({
          apiKey,
          baseURL: `https://${providerConfig.resourceName || azureConfig.resourceName}.openai.azure.com/openai/deployments/${azureConfig.deploymentId}`,
          defaultQuery: { 'api-version': azureConfig.apiVersion || '2024-02-15-preview' },
          defaultHeaders: {
            'api-key': apiKey,
          },
          dangerouslyAllowBrowser: true
        });

      case 'anthropic':
        // For now, we'll use a compatible format through a proxy or conversion layer
        // In a real implementation, you'd use the Anthropic SDK
        throw new Error('Anthropic provider not yet implemented in direct client');

      case 'google':
        // Google AI client would go here
        throw new Error('Google AI provider not yet implemented in direct client');

      case 'mistral':
        // Mistral client (often OpenAI compatible)
        return new OpenAI({
          apiKey,
          baseURL: 'https://api.mistral.ai/v1',
          dangerouslyAllowBrowser: true
        });

      case 'deepseek':
        // DeepSeek client (OpenAI compatible)
        return new OpenAI({
          apiKey,
          baseURL: 'https://api.deepseek.com',
          dangerouslyAllowBrowser: true
        });

      case 'custom':
        // Custom provider
        const customConfig = config as any;
        return new OpenAI({
          apiKey,
          baseURL: customConfig.endpoint || providerConfig.endpoint,
          dangerouslyAllowBrowser: true
        });

      default:
        throw new Error(`Unsupported provider: ${provider}`);
    }
  }

  private async getChatbotAIConfig(chatbotId: string): Promise<AIChatbotSettings | null> {
    try {
      const { data: chatbotData, error } = await supabase
        .from('chatbots')
        .select('ai_settings')
        .eq('id', chatbotId)
        .single();

      if (error || !chatbotData?.ai_settings) {
        console.warn(`No AI settings found for chatbot ${chatbotId}`);
        return null;
      }

      return chatbotData.ai_settings as AIChatbotSettings;
    } catch (error) {
      console.error('Error fetching chatbot AI config:', error);
      return null;
    }
  }

  async getCompletion(params: AICompletionParams, chatbotId?: string): Promise<string> {
    let config = params.config;
    let chatbotSettings: AIChatbotSettings | null = null;

    // Get chatbot AI configuration if chatbotId provided
    if (chatbotId && !config) {
      chatbotSettings = await this.getChatbotAIConfig(chatbotId);
      if (chatbotSettings?.aiServices?.primary) {
        config = chatbotSettings.aiServices.primary;
      }
    }

    // Fall back to global OpenAI if no configuration
    if (!config) {
      config = {
        provider: 'openai',
        model: 'gpt-3.5-turbo',
        temperature: 0.7,
        max_tokens: 1000
      } as OpenAIConfig;
    }

    try {
      const client = await this.getProviderClient(config, chatbotSettings || undefined);

      const completion = await client.chat.completions.create({
        model: config.model,
        messages: params.messages,
        temperature: params.temperature || config.temperature || 0.7,
        max_tokens: params.max_tokens || config.max_tokens || 1000,
        response_format: params.response_format
      });

      return completion.choices[0]?.message?.content || '';
    } catch (error) {
      console.error(`❌ ${config.provider} API Error:`, error);

      // Try fallback if configured
      if (chatbotSettings?.aiServices?.enableFallback && chatbotSettings?.aiServices?.fallback) {
        console.log(`🔄 Attempting fallback to ${chatbotSettings.aiServices.fallback.provider}`);
        try {
          const fallbackClient = await this.getProviderClient(chatbotSettings.aiServices.fallback, chatbotSettings);
          const completion = await fallbackClient.chat.completions.create({
            model: chatbotSettings.aiServices.fallback.model,
            messages: params.messages,
            temperature: params.temperature || chatbotSettings.aiServices.fallback.temperature || 0.7,
            max_tokens: params.max_tokens || chatbotSettings.aiServices.fallback.max_tokens || 1000,
          });
          return completion.choices[0]?.message?.content || '';
        } catch (fallbackError) {
          console.error('❌ Fallback also failed:', fallbackError);
        }
      }

      throw new Error(`AI completion failed: ${error.message || 'Unknown error'}`);
    }
  }

  async getStreamingCompletion(params: AICompletionParams, chatbotId?: string): Promise<ReadableStream<string>> {
    let config = params.config;
    let chatbotSettings: AIChatbotSettings | null = null;

    // Get chatbot AI configuration if chatbotId provided
    if (chatbotId && !config) {
      chatbotSettings = await this.getChatbotAIConfig(chatbotId);
      if (chatbotSettings?.aiServices?.primary) {
        config = chatbotSettings.aiServices.primary;
      }
    }

    // Fall back to global OpenAI if no configuration
    if (!config) {
      config = {
        provider: 'openai',
        model: 'gpt-3.5-turbo',
        temperature: 0.7,
        max_tokens: 1000
      } as OpenAIConfig;
    }

    try {
      const client = await this.getProviderClient(config, chatbotSettings || undefined);

      const stream = await client.chat.completions.create({
        model: config.model,
        messages: params.messages,
        temperature: params.temperature || config.temperature || 0.7,
        max_tokens: params.max_tokens || config.max_tokens || 1000,
        stream: true
      });

      // Convert provider stream to ReadableStream<string>
      return new ReadableStream({
        async start(controller) {
          try {
            for await (const chunk of stream) {
              const content = chunk.choices[0]?.delta?.content || '';
              if (content) {
                controller.enqueue(content);
              }
            }
            controller.close();
          } catch (error) {
            console.error('❌ Streaming error:', error);
            controller.error(error);
          }
        }
      });
    } catch (error) {
      console.error(`❌ ${config.provider} Streaming API Error:`, error);
      throw new Error(`AI streaming failed: ${error.message || 'Unknown error'}`);
    }
  }

  async validateResponse(params: AIValidationParams): Promise<AIValidationResult> {
    let config: ProviderConfig | undefined;
    let chatbotSettings: AIChatbotSettings | null = null;

    // Get chatbot AI configuration for validation
    if (params.chatbotId) {
      chatbotSettings = await this.getChatbotAIConfig(params.chatbotId);
      if (chatbotSettings?.aiServices?.validation) {
        config = chatbotSettings.aiServices.validation;
      } else if (chatbotSettings?.aiServices?.primary) {
        config = chatbotSettings.aiServices.primary;
      }
    }

    // Use provided aiConfig if available
    if (params.aiConfig?.validation) {
      config = params.aiConfig.validation;
    } else if (params.aiConfig?.primary) {
      config = params.aiConfig.primary;
    }

    // Return permissive validation if no AI configuration
    if (!config) {
      return {
        isValid: true,
        confidenceScore: 0.5,
        suggestedFollowUp: "Thank you for your response."
      };
    }

    try {
      // Build validation prompt
      let systemMessage = `You are a response validator for a conversational chatbot.
Your task is to determine if the user's response appropriately answers the question asked.
Return a JSON object with:
- isValid (boolean): whether the response answers the question
- confidenceScore (number between 0-1): how confident you are
- suggestedFollowUp (string): a natural follow-up that acknowledges their answer while steering towards a better answer if needed
- reasonForRejection (string): if invalid, explain why briefly`;

      // Apply validation settings
      if (params.validationSettings) {
        if (params.validationSettings.strictValidation) {
          systemMessage += `\n\nUse strict validation rules. The response must directly and completely answer the question.`;
        }
        if (params.validationSettings.allowPartialMatches) {
          systemMessage += `\n\nAllow partial matches. If the response partially answers the question, consider it valid.`;
        }
        if (params.validationSettings.confidenceThreshold) {
          systemMessage += `\n\nUse a confidence threshold of ${params.validationSettings.confidenceThreshold}. Only mark responses as invalid if your confidence is above this threshold.`;
        }
      }

      // Add knowledge base context if chatbotId is provided
      if (params.chatbotId) {
        try {
          const { data: knowledgeBaseEntries, error } = await supabase
            .from('knowledge_base')
            .select('title, content')
            .eq('chatbot_id', params.chatbotId);

          if (!error && knowledgeBaseEntries && knowledgeBaseEntries.length > 0) {
            const knowledgeBaseContext = knowledgeBaseEntries
              .map(entry => `${entry.title}:\n${entry.content}`)
              .join('\n\n');

            systemMessage += `\n\nBase your validation on this domain knowledge as well:\n${knowledgeBaseContext}`;
          }
        } catch (error) {
          console.warn('Could not fetch knowledge base for validation:', error);
        }
      }

      const userMessage = `Question asked: "${params.question}"
Question type: ${params.questionType || 'text'}
User's response: "${params.response}"
Previous exchanges: ${JSON.stringify(params.conversationContext || [])}`;

      const completion = await this.getCompletion({
        messages: [
          { role: 'system', content: systemMessage },
          { role: 'user', content: userMessage }
        ],
        config,
        response_format: { type: 'json_object' }
      }, params.chatbotId);

      const validationResult = JSON.parse(completion) as AIValidationResult;

      // Ensure required fields are present
      return {
        isValid: validationResult.isValid ?? true,
        confidenceScore: validationResult.confidenceScore ?? 0.5,
        suggestedFollowUp: validationResult.suggestedFollowUp || "Thank you for your response.",
        reasonForRejection: validationResult.reasonForRejection,
        extractedValue: validationResult.extractedValue
      };

    } catch (error) {
      console.error('❌ AI Validation Error:', error);
      // Return permissive validation on error
      return {
        isValid: true,
        confidenceScore: 0.5,
        suggestedFollowUp: "Thank you for your response."
      };
    }
  }

  async generateChatResponse(
    messages: AIMessage[],
    chatbotId?: string,
    knowledgeBaseContext?: string
  ): Promise<string> {
    let chatbotSettings: AIChatbotSettings | null = null;
    let config: ProviderConfig | undefined;

    // Get chatbot AI configuration
    if (chatbotId) {
      chatbotSettings = await this.getChatbotAIConfig(chatbotId);
      if (chatbotSettings?.aiServices?.primary) {
        config = chatbotSettings.aiServices.primary;
      }
    }

    try {
      // Get chatbot settings for context
      let systemPrompt = 'You are a helpful assistant.';

      if (chatbotId) {
        try {
          const { data: chatbotData } = await supabase
            .from('chatbots')
            .select('name, company_name')
            .eq('id', chatbotId)
            .single();

          if (chatbotData) {
            const botName = chatbotData.name || 'Assistant';
            const companyName = chatbotData.company_name || '';

            systemPrompt = `You are an assistant for ${botName}`;
            if (companyName) {
              systemPrompt += ` at ${companyName}`;
            }
            systemPrompt += '. Be helpful, clear, and friendly.';
          }
        } catch (error) {
          console.warn('Could not fetch chatbot data:', error);
        }
      }

      // Add knowledge base context if available
      if (knowledgeBaseContext) {
        systemPrompt += `\n\nUse the following information to answer questions:\n\n${knowledgeBaseContext}`;
      }

      const completion = await this.getCompletion({
        messages: [
          { role: 'system', content: systemPrompt },
          ...messages
        ],
        config
      }, chatbotId);

      return completion;
    } catch (error) {
      console.error('❌ Chat response generation failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const directAIClient = new DirectAIClient();
